(() => {
  if (customElements.get('shipping-rate-notice')) return;

  const API_VERSION = '2026-07';
  const CACHE_TTL = 30 * 60 * 1000;
  const requests = new Map();

  const TRANSLATIONS = Object.freeze({
    de: {
      freeDomestic: 'Kostenfreier Versand innerhalb Deutschlands',
      calculating: (country) => `Versand nach ${country} wird berechnet …`,
      unavailable: (country) => `Für ${country} ist derzeit kein Versandtarif verfügbar.`,
      freeInternational: (country) => `Kostenfreier Versand nach ${country}`,
      priced: (country, price, multiple) => `Versand nach ${country}: ${multiple ? 'ab ' : ''}${price}`,
      error: 'Versandpreis konnte gerade nicht geladen werden.',
    },
    en: {
      freeDomestic: 'Free shipping within Germany',
      calculating: (country) => `Calculating shipping to ${country} …`,
      unavailable: (country) => `Shipping is currently unavailable to ${country}.`,
      freeInternational: (country) => `Free shipping to ${country}`,
      priced: (country, price, multiple) => `Shipping to ${country}: ${multiple ? 'from ' : ''}${price}`,
      error: 'The shipping price could not be loaded right now.',
    },
    fr: {
      freeDomestic: 'Livraison gratuite en Allemagne',
      calculating: (country) => `Calcul des frais de livraison vers ${country} …`,
      unavailable: (country) => `La livraison vers ${country} est actuellement indisponible.`,
      freeInternational: (country) => `Livraison gratuite vers ${country}`,
      priced: (country, price, multiple) => `Livraison vers ${country} : ${multiple ? 'à partir de ' : ''}${price}`,
      error: 'Le tarif de livraison ne peut pas être chargé pour le moment.',
    },
  });

  // Valid example postal codes for every currently enabled shipping country
  // that uses one. Countries without postal codes still receive the neutral
  // city/street below. These values are only used for a non-persistent quote.
  const REFERENCE_ZIPS = Object.freeze({
    AD: 'AD100', AF: '1001', AI: '2640', AL: '1001', AM: '375010',
    AT: '1010', AU: '2000', AZ: '1000', BA: '71000', BB: 'BB23026',
    BD: '1340', BE: '4000', BG: '1000', BH: '317', BM: 'FL 07',
    BR: '01001-000', BT: '11001', BY: '223016', CH: '8001', CL: '8340457',
    CN: '100000', CO: '110111', CR: '10101', CV: '7600', CZ: '100 00',
    DE: '26133', DK: '8660', DO: '11903', DZ: '40304', EC: '090105',
    EE: '69501', EG: '4460232', ES: '28001', FI: '00550', FK: 'FIQQ 1ZZ',
    FR: '33380', GB: 'EC1Y 8SY', GE: '0101', GF: '97300', GN: '001',
    GR: '151 24', GT: '09001', GW: '1000', HN: '11101', HR: '10000',
    HT: '6120', HU: '1037', IE: 'A65 F4E2', IL: '9614303', IN: '110001',
    IQ: '31001', IS: '101', IT: '00184', JO: '11937', JP: '100-0001',
    KG: '720001', KR: '03051', KY: 'KY1-1100', KZ: '040900',
    LB: '2038 3054', LI: '9496', LK: '20000', LR: '1000', LS: '100',
    LT: '04340', LU: '4750', LV: 'LV-1073', MA: '53000', MD: '2012',
    ME: '81257', MG: '501', MN: '65030', MT: 'NXR 01', MU: '42602',
    MV: '20026', MX: '06000', MZ: '1102', NA: '10001', NC: '98814',
    NE: '8001', NG: '930283', NI: '52000', NL: '1234 AB', NO: '0025',
    NP: '44601', NZ: '1010', OM: '133', PE: 'LIMA 23', PK: '44000',
    PL: '00-950', PT: '2725-079', PY: '001001', RO: '060274', RS: '106314',
    SA: '11564', SE: '11455', SH: 'STHL 1ZZ', SI: '4000', SK: '010 01',
    SM: '47890', SN: '12500', SO: 'JH 09010', SV: '2101', TJ: '735450',
    TM: '744000', TN: '1002', TR: '01960', TT: '500234', TZ: '6090',
    UA: '15432', US: '10001', UY: '11600', UZ: '702100', VA: '00120',
    VE: '1010', WF: '98600', YT: '97600', ZA: '8001', ZM: '50100',
  });

  const REFERENCE_PROVINCES = Object.freeze({
    AE: 'DU', AU: 'NSW', BR: 'SP', CA: 'ON', CN: 'BJ', CO: 'DC',
    CR: 'SJ', ES: 'M', HN: 'FM', IN: 'DL', IQ: 'AR', IT: 'RM',
    JM: '13', JP: 'JP-13', KN: 'N', KR: '11', KY: 'Cayman Brac',
    MX: 'CMX', NR: '01', NZ: 'AUK', SO: 'AW', SV: 'AH', US: 'NY',
    VE: 'Z', ZA: 'WC',
  });

  function referenceAddress(countryCode) {
    return {
      ...(REFERENCE_ZIPS[countryCode] ? { zip: REFERENCE_ZIPS[countryCode] } : {}),
      ...(REFERENCE_PROVINCES[countryCode]
        ? { provinceCode: REFERENCE_PROVINCES[countryCode] }
        : {}),
    };
  }

  function parsePayloads(response, body) {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('multipart/mixed')) {
      return [JSON.parse(body)];
    }

    const boundaryMatch = contentType.match(/boundary="?([^";]+)"?/i);
    const boundary = boundaryMatch ? `--${boundaryMatch[1]}` : '--graphql';

    return body
      .split(boundary)
      .map((part) => {
        const trimmed = part.trim();
        if (!trimmed || trimmed === '--') return null;

        const headerEnd = trimmed.search(/\r?\n\r?\n/);
        const jsonText = (headerEnd >= 0 ? trimmed.slice(headerEnd) : trimmed)
          .replace(/--$/, '')
          .trim();

        if (!jsonText.startsWith('{')) return null;

        try {
          return JSON.parse(jsonText);
        } catch (_error) {
          return null;
        }
      })
      .filter(Boolean);
  }

  function collectDeliveryOptions(payloads) {
    const options = [];
    const errors = [];

    const addGroups = (groups) => {
      groups?.edges?.forEach(({ node }) => {
        node?.deliveryOptions?.forEach((option) => options.push(option));
      });
    };

    payloads.forEach((payload) => {
      payload.errors?.forEach((error) => errors.push(error));

      const cartCreate = payload.data?.cartCreate;
      cartCreate?.userErrors?.forEach((error) => errors.push(error));

      addGroups(cartCreate?.cart?.deliveryGroups);

      payload.incremental?.forEach((part) => {
        part.errors?.forEach((error) => errors.push(error));
        addGroups(part.data?.deliveryGroups);
      });
    });

    if (errors.length) {
      const error = new Error(errors.map(({ message }) => message).join('; '));
      error.codes = errors.map(({ extensions }) => extensions?.code).filter(Boolean);
      throw error;
    }

    return options.filter((option, index, all) => {
      const amount = option.estimatedCost?.amount;
      return all.findIndex((candidate) => (
        candidate.title === option.title
        && candidate.estimatedCost?.amount === amount
      )) === index;
    });
  }

  async function fetchRates({ shopDomain, variantId, countryCode, address = null }) {
    const deliveryAddress = {
      countryCode,
      ...(address || {}),
    };

    if (address !== null) {
      deliveryAddress.city = 'Shipping quote';
      deliveryAddress.address1 = '1 Shipping quote';
    }

    const query = `
      mutation ShippingQuote($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            deliveryGroups(first: 10) {
              edges {
                node {
                  deliveryOptions {
                    title
                    estimatedCost {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const response = await fetch(`https://${shopDomain}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          input: {
            lines: [{
              merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
              quantity: 1,
            }],
            buyerIdentity: { countryCode },
            delivery: {
              addresses: [{
                address: { deliveryAddress },
                oneTimeUse: true,
                selected: true,
              }],
            },
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Shipping quote failed with HTTP ${response.status}`);
    }

    return collectDeliveryOptions(parsePayloads(response, await response.text()));
  }

  class ShippingRateNotice extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('[data-shipping-rate-message]');
      this.detailsLink = this.querySelector('[data-shipping-rate-link]');
      this.countryCode = (this.dataset.countryCode || 'DE').toUpperCase();
      this.countryName = this.dataset.countryName || this.countryCode;
      this.language = (document.documentElement.lang || 'de').toLowerCase().split('-')[0];
      this.copy = TRANSLATIONS[this.language] || TRANSLATIONS.en;
      this.scope = this.closest('.product__blocks') || this.parentElement;
      this.lastVariantId = null;

      this.onProductChange = () => {
        window.setTimeout(() => this.refresh(), 0);
      };

      this.scope?.addEventListener('change', this.onProductChange);
      this.refresh();
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('change', this.onProductChange);
    }

    currentVariantId() {
      const input = this.scope?.querySelector('input[name="id"]');
      return String(input?.value || this.dataset.variantId || '');
    }

    cacheKey(variantId) {
      return `kp-shipping-rate:${variantId}:${this.countryCode}`;
    }

    readCache(key) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key));
        if (cached && Date.now() - cached.savedAt < CACHE_TTL) return cached.options;
      } catch (_error) {
        // Ignore unavailable or malformed session storage.
      }
      return null;
    }

    writeCache(key, options) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ options, savedAt: Date.now() }));
      } catch (_error) {
        // A live quote still works if session storage is unavailable.
      }
    }

    setMessage(text, showDetails = false) {
      if (this.message) this.message.textContent = text;
      if (this.detailsLink) this.detailsLink.hidden = !showDetails;
    }

    formatMoney(amount, currencyCode) {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currencyCode,
      }).format(Number(amount));
    }

    async loadRates(variantId) {
      const key = this.cacheKey(variantId);
      const cached = this.readCache(key);
      if (cached) return cached;

      if (!requests.has(key)) {
        requests.set(key, (async () => {
          let options = [];

          try {
            options = await fetchRates({
              shopDomain: this.dataset.shopDomain,
              variantId,
              countryCode: this.countryCode,
            });
          } catch (_error) {
            // Some destinations require a postal code or province before rates resolve.
          }

          if (!options.length) {
            options = await fetchRates({
              shopDomain: this.dataset.shopDomain,
              variantId,
              countryCode: this.countryCode,
              address: referenceAddress(this.countryCode),
            });
          }

          this.writeCache(key, options);
          return options;
        })().finally(() => requests.delete(key)));
      }

      return requests.get(key);
    }

    async refresh() {
      const variantId = this.currentVariantId();
      if (!variantId || variantId === this.lastVariantId) return;
      this.lastVariantId = variantId;

      if (this.countryCode === 'DE') {
        this.setMessage(this.copy.freeDomestic);
        return;
      }

      this.setMessage(this.copy.calculating(this.countryName));

      try {
        const options = await this.loadRates(variantId);
        if (variantId !== this.currentVariantId()) return;

        if (!options.length) {
          this.setMessage(this.copy.unavailable(this.countryName), true);
          return;
        }

        const sorted = [...options].sort((a, b) => (
          Number(a.estimatedCost.amount) - Number(b.estimatedCost.amount)
        ));
        const cheapest = sorted[0];
        const amount = Number(cheapest.estimatedCost.amount);

        if (amount === 0) {
          this.setMessage(this.copy.freeInternational(this.countryName), true);
          return;
        }

        const price = this.formatMoney(amount, cheapest.estimatedCost.currencyCode);
        this.setMessage(this.copy.priced(this.countryName, price, sorted.length > 1), true);
      } catch (_error) {
        this.setMessage(this.copy.error, true);
      }
    }
  }

  customElements.define('shipping-rate-notice', ShippingRateNotice);
})();
